import { isPreSale } from 'lib/offer';

import { insertInArray } from '../array';
import { toAbsoluteLink, toCategoryLink, toSubcategoryLink } from '../links';
import { toFormattedJsonLdString } from '../string';

import APP from 'constants/app';
import OFFER from 'constants/offer';
import SCHEMAS from 'constants/schemas';

export const appendDynamicSubcategoryToBreadcrumbSchema = (
  breadcrumbSchema,
  subcategoryName,
  subcategorySlug
) => {
  if (!subcategoryName) {
    return breadcrumbSchema;
  }

  const breadcrumbSchemaArr = insertInArray({
    array: breadcrumbSchema,
    position: 2,
    item: {
      position: 3,
      name: subcategoryName,
      item: toAbsoluteLink(toSubcategoryLink(subcategorySlug)),
    },
  });

  breadcrumbSchemaArr[3].position = 4;

  return breadcrumbSchemaArr;
};

export const formatDiscussionForumPostingAsJsonLd = ({
  authorName,
  commentsCount,
  likesCount,
  publishedAt,
  textRaw,
  title,
}) => ({
  '@context': 'https://schema.org',
  '@type': 'DiscussionForumPosting',
  headline: title,
  text: textRaw,
  author: {
    '@type': 'Person',
    name: authorName,
  },
  datePublished: publishedAt,
  interactionStatistic: [
    {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/CommentAction',
      userInteractionCount: commentsCount,
    },
    {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/LikeAction',
      userInteractionCount: likesCount,
    },
  ],
});

const formatSellerAsJsonLd = ({ name, url }) => ({
  '@type': 'Organization',
  '@context': 'https://schema.org',
  name,
  url,
});

export const formatOfferAsJsonLd = ({
  price,
  priceCurrency,
  availability,
  url,
  offerCount,
  lowPrice,
  highPrice,
  category,
  seller,
}) => ({
  '@type': 'Offer',
  '@context': 'https://schema.org',
  availability,
  category,
  highPrice,
  itemCondition: 'NewCondition',
  lowPrice,
  offerCount,
  price,
  priceCurrency,
  seller: formatSellerAsJsonLd(seller),
  url,
});

export const formatProductAsJsonLd = ({
  description,
  images,
  offers,
  productName,
  sku,
}) => ({
  '@type': 'Product',
  '@context': 'https://schema.org',
  sku,
  image: images,
  offers: offers.map(formatOfferAsJsonLd),
  name: productName,
  description,
});

export const formatItemListAsJsonLd = ({ position, item, url }) => ({
  '@type': 'ListItem',
  '@context': 'https://schema.org',
  position,
  url,
  item,
});

export const fromStatusToAvailabilityJsonLd = (offerStatusName, offerTags) => {
  if (offerStatusName === OFFER.STATUSES.FINISHED) {
    return SCHEMAS.AVAILABILITIES.SOLD_OUT;
  }

  if (isPreSale(offerTags)) {
    return SCHEMAS.AVAILABILITIES.PRE_SALE;
  }

  return SCHEMAS.AVAILABILITIES.LIMITED_AVAILABILITY;
};

export const getCategoryBreadcrumbArr = ({
  categoryName,
  categorySlug,
  subcategoryName = null,
  subcategorySlug,
  deepSubcategoryName = null,
  deepSubcategorySlug,
}) => {
  const baseBreadcrumbArr = [
    {
      position: 1,
      name: 'Promoções',
      item: APP.URL,
    },
    {
      position: 2,
      name: categoryName,
      item: toAbsoluteLink(toCategoryLink(categorySlug)),
    },
  ];

  if (subcategoryName) {
    baseBreadcrumbArr.push({
      position: 3,
      name: subcategoryName,
      item: toAbsoluteLink(toSubcategoryLink(subcategorySlug)),
    });
  }

  if (deepSubcategoryName) {
    baseBreadcrumbArr.push({
      position: 4,
      name: deepSubcategoryName,
      item: toAbsoluteLink(toSubcategoryLink(deepSubcategorySlug)),
    });
  }

  return baseBreadcrumbArr;
};

export const toReviewSchema = (reviews, offerTitle = '') =>
  reviews.map(({ raw, date, user, stars }) => ({
    datePublished: date,
    name: offerTitle && `Review de ${toFormattedJsonLdString(offerTitle)}`,
    reviewBody: toFormattedJsonLdString(raw.replace(/\n/g, '')),
    author: {
      type: 'Person',
      name: user.username,
    },
    publisher: {
      type: 'Organization',
      name: APP.NAME,
    },
    reviewRating: {
      bestRating: '5',
      ratingValue: stars || OFFER.REVIEWS.DEFAULT_RATING_VALUE,
      worstRating: '1',
    },
  }));
